// Footer — full landing footer | logo + description / platform + company columns / early access CTA | gradient glow above border

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { colors } from './landingTokens'
import { useWindowWidth } from '../../hooks/useWindowWidth'
import { CALENDLY_URL } from '../../constants'

const openExternal = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

// ──── NavLink ─────────────────────────────────────────────────

const NavLink: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => {
  const [hovered, setHovered] = useState(false)
  return (
    <span
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'block',
        fontFamily: 'Inter, sans-serif',
        fontSize: 13,
        color: hovered ? '#FFFFFF' : 'rgba(255,255,255,0.6)',
        marginBottom: 12,
        cursor: 'pointer',
        transition: 'color 0.2s ease',
      }}
    >
      {label}
    </span>
  )
}

// ──── ColumnHeader ────────────────────────────────────────────

const ColumnHeader: React.FC<{ label: string }> = ({ label }) => (
  <p
    style={{
      fontFamily: 'Inter, sans-serif',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.5)',
      marginBottom: 20,
      margin: '0 0 20px 0',
    }}
  >
    {label}
  </p>
)

// ──── Footer ──────────────────────────────────────────────────

function Footer({ onOpen }: { onOpen?: () => void }) {
  const navigate = useNavigate()
  const width = useWindowWidth()
  const isMobile = width < 768
  const isTablet = width >= 768 && width < 1024

  return (
    <footer
      style={{
        background: 'transparent',
        position: 'relative',
        zIndex: 1,
        overflow: 'hidden',
        paddingTop: isMobile ? 60 : 120,
        paddingBottom: isMobile ? 40 : 80,
      }}
    >
      {/* Main content */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 1100,
          margin: '0 auto',
          padding: isMobile ? '0 24px' : '0 40px',
        }}
      >
        {/* Top section */}
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: isMobile ? 40 : isTablet ? 40 : 80,
            paddingBottom: 64,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {/* Left block */}
          <div style={{ maxWidth: isMobile ? 'none' : 320, width: isMobile ? '100%' : 'auto' }}>
            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3 }} aria-hidden="true">
                <div className="w-[5px] h-[14px] rounded-full bg-gradient-to-t from-violet-600 via-violet-500 to-violet-300 shadow-[0_0_8px_rgba(139,92,246,0.6)]" />
                <div className="w-[5px] h-[22px] rounded-full bg-gradient-to-t from-violet-600 via-violet-400 to-violet-200 shadow-[0_0_10px_rgba(139,92,246,0.7)]" />
                <div className="w-[5px] h-[16px] rounded-full bg-gradient-to-t from-violet-600 via-violet-500 to-violet-300 shadow-[0_0_8px_rgba(139,92,246,0.6)]" />
              </div>
              <span
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 16,
                  fontWeight: 600,
                  color: colors.textPrimary,
                  letterSpacing: '-0.01em',
                }}
              >
                Triptic
              </span>
            </div>

            {/* Description */}
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.7,
                marginTop: 16,
                marginBottom: 0,
              }}
            >
              Triptic is the AI workspace for college counseling — students, counselors, and institutions working in one place.
            </p>

            {/* Social links */}
            <div style={{ display: 'flex', gap: 16, marginTop: 24 }}>
              {['LinkedIn', 'Twitter', 'Instagram'].map((s) => (
                <span
                  key={s}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          {/* Right columns */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 40 : isTablet ? 40 : 80, width: isMobile ? '100%' : 'auto' }}>
            {/* Platform */}
            <div>
              <ColumnHeader label="Platform" />
              <NavLink label="Overview"     onClick={() => navigate('/')}                  />
              <NavLink label="Students"     onClick={() => navigate('/student-intake')}    />
              <NavLink label="Counselors"   onClick={() => openExternal(CALENDLY_URL)} />
              <NavLink label="Institutions" onClick={() => openExternal(CALENDLY_URL)} />
            </div>

            {/* Company */}
            <div>
              <ColumnHeader label="Company" />
              <NavLink label="Privacy Policy"  onClick={() => navigate('/privacy-policy')}  />
              <NavLink label="Terms of Service" onClick={() => navigate('/terms-of-service')} />
            </div>

            {/* Early access */}
            <div>
              <ColumnHeader label="For Students" />
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.6)',
                  marginBottom: 16,
                  marginTop: 0,
                }}
              >
                Get started with a free intro call.
              </p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="landing-cta landing-cta--student"
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                Get Started →
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          style={{
            marginTop: 40,
            padding: '24px 0',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'center' : 'space-between',
            alignItems: 'center',
            gap: isMobile ? 8 : 0,
          }}
        >
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              color: 'rgba(255,255,255,0.4)',
              margin: 0,
              textAlign: isMobile ? 'center' : undefined,
            }}
          >
            © 2026 Triptic.ai, Inc. All rights reserved.
          </p>
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              color: 'rgba(255,255,255,0.35)',
              fontStyle: 'italic',
              margin: 0,
              textAlign: isMobile ? 'center' : undefined,
            }}
          >
            Made for students, counselors, and institutions.
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
